import * as React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import RtlButton from './RtlButton';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
  GridEventListener,
  gridClasses,
  GridColumnVisibilityModel,
} from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PaymentIcon from '@mui/icons-material/Payment';
import CancelIcon from '@mui/icons-material/Cancel';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Popover from '@mui/material/Popover';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { useDialogs } from '../hooks/useDialogs/useDialogs';
import { useNotification } from './CustomNotification';
import {
  deleteOne as deleteOrder,
  getMany as getOrders,
  type Order,
  type OrderStatus,
} from '../data/orders';
import PageContainer from './PageContainer';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import PaymentDialog from './PaymentDialog';
import InstallmentPaymentDialog from './InstallmentPaymentDialog';
import InstallmentSelectionDialog from './InstallmentSelectionDialog';
import CancelOrderDialog from './CancelOrderDialog';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import DateRangeFilter from './DateRangeFilter';
import CustomTablePagination from './CustomTablePagination';
import dayjs from 'dayjs';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { getAllEmployees, type Employee } from '../data/employees';

const INITIAL_PAGE_SIZE = 25;

// Payments and installments require a cost and a total amount on the order.
// Compare numerically: cost and full_amount arrive as strings, so "0.00" is truthy.
function hasRequiredAmounts(order: Order | null): boolean {
  if (!order) {
    return false;
  }
  return Number(order.cost ?? 0) > 0 && Number(order.full_amount ?? 0) > 0;
}

export default function OrderList() {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, direction } = useLanguage();
  const { user } = useAuth();

  const dialogs = useDialogs();
  const notifications = useNotification();

  const [paginationModel, setPaginationModel] = React.useState<GridPaginationModel>({
    page: searchParams.get('page') ? Number(searchParams.get('page')) : 0,
    pageSize: searchParams.get('pageSize')
      ? Number(searchParams.get('pageSize'))
      : INITIAL_PAGE_SIZE,
  });
  const [filterModel, setFilterModel] = React.useState<GridFilterModel>(
    searchParams.get('filter')
      ? JSON.parse(searchParams.get('filter') ?? '')
      : { items: [] },
  );
  const [sortModel, setSortModel] = React.useState<GridSortModel>(
    searchParams.get('sort') ? JSON.parse(searchParams.get('sort') ?? '') : [],
  );

  const [rowsState, setRowsState] = React.useState<{
    rows: Order[];
    rowCount: number;
  }>({
    rows: [],
    rowCount: 0,
  });

  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | null>(null);
  const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [selectedOrder, setSelectedOrder] = React.useState<Order | null>(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = React.useState(false);
  const [installmentSelectionDialogOpen, setInstallmentSelectionDialogOpen] = React.useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = React.useState(false);
  const [columnMenuAnchor, setColumnMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [dateFrom, setDateFrom] = React.useState<string | null>(
    searchParams.get('date_from') || null,
  );
  const [dateTo, setDateTo] = React.useState<string | null>(
    searchParams.get('date_to') || null,
  );
  const [statusFilter, setStatusFilter] = React.useState<OrderStatus | ''>(
    (searchParams.get('status') as OrderStatus) || '',
  );
  const [mobileNumber, setMobileNumber] = React.useState(
    searchParams.get('mobile_number') || '',
  );
  const [customerName, setCustomerName] = React.useState(
    searchParams.get('name') || '',
  );
  const [employees, setEmployees] = React.useState<Employee[]>([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = React.useState<number | null>(
    searchParams.get('user_id') ? Number(searchParams.get('user_id')) : null,
  );

  // Fetch employees for the autocomplete filter (only for GM & BM roles)
  const canAccessEmployeeFilter = user?.role === 'GM' || user?.role === 'BM';
  React.useEffect(() => {
    if (!canAccessEmployeeFilter) return;
    const fetchEmployees = async () => {
      try {
        const employeesList = await getAllEmployees();
        setEmployees(employeesList);
      } catch (err) {
        console.error('Failed to fetch employees:', err);
      }
    };
    fetchEmployees();
  }, [canAccessEmployeeFilter]);

  // Default column visibility - specify which columns to hide by default
  const defaultColumnVisibility: GridColumnVisibilityModel = {
    customerNumber: false,  // Hidden by default
    user: false,            // Hidden by default
    people_number: false,   // Hidden by default
    cost: false,            // Hidden by default
    paid_amount: false,     // Hidden by default
    created_at: false,      // Hidden by default
  };

  // Column visibility state - load from localStorage or use defaults
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState<GridColumnVisibilityModel>(() => {
    const saved = localStorage.getItem('orderListColumnVisibility');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return defaultColumnVisibility;
      }
    }
    return defaultColumnVisibility;
  });

  // Save column visibility to localStorage whenever it changes
  const handleColumnVisibilityModelChange = React.useCallback(
    (newModel: GridColumnVisibilityModel) => {
      setColumnVisibilityModel(newModel);
      localStorage.setItem('orderListColumnVisibility', JSON.stringify(newModel));
    },
    [],
  );

  const handlePaginationModelChange = React.useCallback(
    (model: GridPaginationModel) => {
      setPaginationModel(model);

      searchParams.set('page', String(model.page));
      searchParams.set('pageSize', String(model.pageSize));

      const newSearchParamsString = searchParams.toString();

      navigate(
        `${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`,
      );
    },
    [navigate, pathname, searchParams],
  );

  const handleFilterModelChange = React.useCallback(
    (model: GridFilterModel) => {
      setFilterModel(model);

      if (
        model.items.length > 0 ||
        (model.quickFilterValues && model.quickFilterValues.length > 0)
      ) {
        searchParams.set('filter', JSON.stringify(model));
      } else {
        searchParams.delete('filter');
      }

      const newSearchParamsString = searchParams.toString();

      navigate(
        `${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`,
      );
    },
    [navigate, pathname, searchParams],
  );

  const handleSortModelChange = React.useCallback(
    (model: GridSortModel) => {
      setSortModel(model);

      if (model.length > 0) {
        searchParams.set('sort', JSON.stringify(model));
      } else {
        searchParams.delete('sort');
      }

      const newSearchParamsString = searchParams.toString();

      navigate(
        `${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`,
      );
    },
    [navigate, pathname, searchParams],
  );

  const handleDateFromChange = React.useCallback(
    (date: string | null) => {
      setDateFrom(date);

      if (date) {
        searchParams.set('date_from', date);
      } else {
        searchParams.delete('date_from');
      }

      const newSearchParamsString = searchParams.toString();

      navigate(
        `${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`,
      );
    },
    [navigate, pathname, searchParams],
  );

  const handleDateToChange = React.useCallback(
    (date: string | null) => {
      setDateTo(date);

      if (date) {
        searchParams.set('date_to', date);
      } else {
        searchParams.delete('date_to');
      }

      const newSearchParamsString = searchParams.toString();

      navigate(
        `${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`,
      );
    },
    [navigate, pathname, searchParams],
  );

  const handleStatusFilterChange = React.useCallback(
    (status: OrderStatus | '') => {
      setStatusFilter(status);

      if (status) {
        searchParams.set('status', status);
      } else {
        searchParams.delete('status');
      }

      const newSearchParamsString = searchParams.toString();

      navigate(
        `${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`,
      );
    },
    [navigate, pathname, searchParams],
  );

  const handleMobileNumberChange = React.useCallback(
    (value: string) => {
      setMobileNumber(value);
      setPaginationModel((p) => ({ ...p, page: 0 }));

      if (value) {
        searchParams.set('mobile_number', value);
      } else {
        searchParams.delete('mobile_number');
      }

      const newSearchParamsString = searchParams.toString();
      navigate(`${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`);
    },
    [navigate, pathname, searchParams],
  );

  const handleCustomerNameChange = React.useCallback(
    (value: string) => {
      setCustomerName(value);
      setPaginationModel((p) => ({ ...p, page: 0 }));

      if (value) {
        searchParams.set('name', value);
      } else {
        searchParams.delete('name');
      }

      const newSearchParamsString = searchParams.toString();
      navigate(`${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`);
    },
    [navigate, pathname, searchParams],
  );

  const handleEmployeeFilterChange = React.useCallback(
    (employeeId: number | null) => {
      setSelectedEmployeeId(employeeId);
      setPaginationModel((p) => ({ ...p, page: 0 }));

      if (employeeId) {
        searchParams.set('user_id', String(employeeId));
      } else {
        searchParams.delete('user_id');
      }

      const newSearchParamsString = searchParams.toString();
      navigate(`${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`);
    },
    [navigate, pathname, searchParams],
  );

  const loadData = React.useCallback(async () => {
    setError(null);
    setIsLoading(true);

    try {
      // Use selected employee ID from filter, or fall back to role-based logic for Employee role
      const userId = selectedEmployeeId ?? (user?.role === 'Employee' ? user.id : undefined);

      // Always use created_at DESC for sorting, regardless of other filters
      const sortBy = 'created_at';
      const sortOrder: 'asc' | 'desc' = 'desc';

      const listData = await getOrders({
        paginationModel,
        sortModel,
        filterModel,
        userId,
        sortBy,
        sortOrder,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        status: statusFilter || undefined,
        mobileNumber: mobileNumber || undefined,
        customerName: customerName || undefined,
      });

      setRowsState({
        rows: listData.items,
        rowCount: listData.itemCount,
      });
    } catch (listDataError) {
      setError(listDataError as Error);
    }

    setIsLoading(false);
  }, [paginationModel, sortModel, filterModel, user, dateFrom, dateTo, statusFilter, mobileNumber, customerName, selectedEmployeeId]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRefresh = React.useCallback(() => {
    if (!isLoading) {
      loadData();
    }
  }, [isLoading, loadData]);

  const handleRowClick = React.useCallback<GridEventListener<'rowClick'>>(
    ({ row }) => {
      navigate(`/orders/${row.id}`);
    },
    [navigate],
  );

  const handleCreateClick = React.useCallback(() => {
    navigate('/orders/new');
  }, [navigate]);

  const handleColumnMenuOpen = React.useCallback((event: React.MouseEvent<HTMLElement>) => {
    setColumnMenuAnchor(event.currentTarget);
  }, []);

  const handleColumnMenuClose = React.useCallback(() => {
    setColumnMenuAnchor(null);
  }, []);

  const handleToggleColumn = React.useCallback(
    (field: string) => {
      setColumnVisibilityModel((prev) => {
        const newModel = {
          ...prev,
          [field]: prev[field] === false ? true : false,
        };
        localStorage.setItem('orderListColumnVisibility', JSON.stringify(newModel));
        return newModel;
      });
    },
    [],
  );

  // Define available columns for visibility toggle
  const availableColumns = React.useMemo(() => [
    { field: 'customer', label: t('orders.customer') },
    { field: 'customerNumber', label: t('orders.customerNumber') },
    { field: 'user', label: t('orders.createdBy') },
    { field: 'people_number', label: t('orders.people_number') },
    { field: 'destination', label: t('orders.destination') },
    { field: 'cost', label: t('orders.cost') },
    { field: 'full_amount', label: t('orders.totalAmount') },
    { field: 'paid_amount', label: t('orders.paidAmount') },
    { field: 'pending_amount', label: t('orders.pendingAmount') },
    { field: 'status', label: t('orders.status') },
    { field: 'created_at', label: t('orders.createdAt') },
  ], [t]);

  const handleRowEdit = React.useCallback(
    (order: Order) => () => {
      navigate(`/orders/${order.id}/edit`);
    },
    [navigate],
  );

  const handleMenuOpen = React.useCallback(
    (event: React.MouseEvent<HTMLElement>, order: Order) => {
      event.stopPropagation();
      setMenuAnchor(event.currentTarget);
      setSelectedOrder(order);
    },
    [],
  );

  const handleMenuClose = React.useCallback(() => {
    setMenuAnchor(null);
    // Don't clear selectedOrder here - it's needed for the dialog
  }, []);

  const handlePayNow = React.useCallback(() => {
    const order = selectedOrder;
    setMenuAnchor(null);
    if (order) {
      // Check if order is cancelled or completed
      if (order.status === 'CANCELED' || order.status === 'COMPLETED') {
        notifications.show(
          t('orders.cannotPayCancelledOrCompleted'),
          {
            severity: 'error',
            autoHideDuration: 3000,
          },
        );
        return;
      }

      // Validate cost and full_amount are set
      const cost = typeof order.cost === 'string' ? parseFloat(order.cost) : order.cost;
      const fullAmount = typeof order.full_amount === 'string' ? parseFloat(order.full_amount) : order.full_amount;

      if (!cost || cost <= 0 || !fullAmount || fullAmount <= 0) {
        notifications.show(
          t('orders.costAndAmountRequired'),
          {
            severity: 'error',
            autoHideDuration: 3000,
          },
        );
        return;
      }

      const paidAmount = typeof order.paid_amount === 'string'
        ? parseFloat(order.paid_amount)
        : order.paid_amount || 0;

      if (paidAmount > 0) {
        // Show installment selection dialog
        setInstallmentSelectionDialogOpen(true);
      } else {
        // Show full payment dialog
        setPaymentDialogOpen(true);
      }
    }
  }, [selectedOrder, notifications, t]);

  const handlePaymentDialogClose = React.useCallback(() => {
    setPaymentDialogOpen(false);
    setTimeout(() => setSelectedOrder(null), 100);
    loadData(); // Refresh the list after payment
  }, [loadData]);

  const handleCancelOrder = React.useCallback(() => {
    setMenuAnchor(null);
    setCancelDialogOpen(true);
  }, []);

  const handleCancelDialogClose = React.useCallback(() => {
    setCancelDialogOpen(false);
    setTimeout(() => setSelectedOrder(null), 100);
  }, []);

  const handleCancelSuccess = React.useCallback(() => {
    setCancelDialogOpen(false);
    setTimeout(() => setSelectedOrder(null), 100);
    loadData(); // Refresh the list after cancellation
  }, [loadData]);

  const handleRowDelete = React.useCallback(
    async (order: Order) => {
      handleMenuClose();
      const confirmed = await dialogs.confirm(
        t('messages.deleteConfirm'),
        {
          title: t('orders.delete'),
          severity: 'error',
          okText: t('common.delete'),
          cancelText: t('common.cancel'),
        },
      );

      if (confirmed) {
        setIsLoading(true);
        try {
          await deleteOrder(Number(order.id));

          notifications.show(t('messages.deleteSuccess'), {
            severity: 'success',
            autoHideDuration: 3000,
          });
          loadData();
        } catch (deleteError) {
          notifications.show(
            `${t('messages.errorOccurred')}: ${(deleteError as Error).message}`,
            {
              severity: 'error',
              autoHideDuration: 3000,
            },
          );
        }
        setIsLoading(false);
      }
    },
    [dialogs, notifications, loadData, t, handleMenuClose],
  );

  const initialState = React.useMemo(
    () => ({
      pagination: { paginationModel: { pageSize: INITIAL_PAGE_SIZE } },
    }),
    [],
  );

  const isRtl = direction === 'rtl';

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'CREATED':
        return '#ff9800'; // Yellow/Orange
      case 'BOOKED':
        return '#4caf50'; // Green
      case 'CANCELED':
        return '#f44336'; // Red
      case 'COMPLETED':
        return '#2196f3'; // Blue
      default:
        return '#757575'; // Gray
    }
  };

  const formatAmount = (amount: string | number | undefined) => {
    if (!amount) return '0.00';
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return numAmount.toFixed(2);
  };

  const columns = React.useMemo<GridColDef[]>(
    () => [
      {
        field: 'customer',
        headerName: t('orders.customer'),
        width: 100,
        minWidth: 80,
        flex: 0.8,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value: { name?: string } | null | undefined) => value?.name || '',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
      },
      {
        field: 'customerNumber',
        headerName: t('orders.customerNumber'),
        width: 100,
        minWidth: 90,
        flex: 0.8,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value, row) => (row.customer?.mobile_number || '').replace(/\s+/g, ''),
        renderCell: (params) => <span dir="ltr">{params.value}</span>,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
      },
      {
        field: 'user',
        headerName: t('orders.createdBy'),
        width: 100,
        minWidth: 90,
        flex: 0.8,
        valueGetter: (value: { name?: string } | null | undefined) => value?.name || '',
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
      },
      {
        field: 'people_number',
        headerName: t('orders.people_number'),
        type: 'number',
        width: 80,
        minWidth: 70,
        flex: 0.5,
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
      },
      {
        field: 'destination',
        headerName: t('orders.destination'),
        width: 100,
        minWidth: 80,
        flex: 0.8,
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
      },
      {
        field: 'cost',
        headerName: t('orders.cost'),
        width: 100,
        minWidth: 90,
        flex: 0.7,
        align: 'center',
        headerAlign: 'center',
        valueFormatter: (value) => formatAmount(value),
        renderCell: (params) => {
          const currencySymbol = direction === 'rtl' ? 'د.إ' : 'AED';
          return `${formatAmount(params.value)} ${currencySymbol}`;
        },
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
      },
      {
        field: 'full_amount',
        headerName: t('orders.totalAmount'),
        width: 100,
        minWidth: 90,
        flex: 0.7,
        align: 'center',
        headerAlign: 'center',
        valueFormatter: (value) => formatAmount(value),
        renderCell: (params) => {
          const currencySymbol = direction === 'rtl' ? 'د.إ' : 'AED';
          return `${formatAmount(params.value)} ${currencySymbol}`;
        },
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
      },
      {
            field: 'paid_amount',
            headerName: t('orders.paidAmount'),
            width: 100,
            minWidth: 90,
            flex: 0.7,
            align: 'center',
            headerAlign: 'center',
            valueGetter: (value) => formatAmount(value),
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            resizable: false,
        },
      {
        field: 'pending_amount',
        headerName: t('orders.pendingAmount'),
        width: 100,
        minWidth: 90,
        flex: 0.7,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value, row) => {
          const fullAmount = typeof row.full_amount === 'string'
            ? parseFloat(row.full_amount)
            : row.full_amount || 0;
          const paidAmount = typeof row.paid_amount === 'string'
            ? parseFloat(row.paid_amount)
            : row.paid_amount || 0;
          return fullAmount - paidAmount;
        },
        valueFormatter: (value) => formatAmount(value),
        renderCell: (params) => {
          const currencySymbol = direction === 'rtl' ? 'د.إ' : 'AED';
          return `${formatAmount(params.value)} ${currencySymbol}`;
        },
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
      },
      {
        field: 'status',
        headerName: t('orders.status'),
        width: 110,
        minWidth: 100,
        flex: 0.7,
        align: 'center',
        headerAlign: 'center',
        type: 'singleSelect',
        valueOptions: ['PENDING', 'CONFIRMED', 'CANCELED', 'COMPLETED'],
        renderCell: (params) => {
          const status = params.value as string;
          const color = getStatusColor(status);
          return (
            <Chip
              label={t(`orders.${status.toLowerCase()}`)}
              size="small"
              sx={{
                backgroundColor: color,
                color: 'white',
                fontWeight: 600,
                minWidth: 90,
              }}
            />
          );
        },
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
      },
      {
        field: 'created_at',
        headerName: t('orders.createdAt'),
        width: 120,
        minWidth: 110,
        flex: 0.8,
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
        valueGetter: (value, row) =>
          dayjs(row.created_at).format('YYYY-MM-DD'),
      },
      {
        field: 'actions',
        type: 'actions',
        width: 50,
        minWidth: 50,
        align: 'center',
        headerAlign: 'center',
        headerName: '',
        getActions: ({ row }) => {
          const hasActions = row.status !== 'CANCELED' && row.status !== 'COMPLETED';
          if (!hasActions) {
            return [];
          }
          return [
            <GridActionsCellItem
              key="menu-item"
              icon={<MoreVertIcon />}
              label={t('common.actions')}
              onClick={(e) => handleMenuOpen(e, row)}
            />,
          ];
        },
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
      },
    ],
    [handleMenuOpen, t, isRtl, direction],
  );

  const pageTitle = t('orders.title');

  return (
    <PageContainer
      breadcrumbs={[{ title: pageTitle }]}
      actions={
        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip title={t('common.refresh')} placement="bottom" enterDelay={1000}>
            <div>
              <IconButton size="small" aria-label="refresh"
              onClick={handleRefresh}
              sx={{ marginLeft:  isRtl? 1 : 0 , }}>
                <RefreshIcon />
              </IconButton>
            </div>
          </Tooltip>
          <Tooltip title="Toggle Columns" placement="bottom" enterDelay={1000}>
            <div>
              <IconButton
                size="small"
                aria-label="toggle columns"
                onClick={handleColumnMenuOpen}
              >
                <ViewColumnIcon />
              </IconButton>
            </div>
          </Tooltip>
          <RtlButton
            variant="contained"
            onClick={handleCreateClick}
            startIcon={<AddIcon />}
            sx={{ background: '#028bcb !important', boxShadow: 'unset !important', border: '0 !important', '&:hover': { background: '#0277a8 !important' } }}
          >
            {t('common.create')}
          </RtlButton>
        </Stack>
      }
    >
      <Box
        sx={{
          flex: 1,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          overflow: 'hidden',
          minHeight: 0,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 2, px: 1, flexShrink: 0, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder={t('orders.customer')}
            value={customerName}
            onChange={(e) => handleCustomerNameChange(e.target.value)}
            sx={{ minWidth: 140 }}
          />
          <TextField
            size="small"
            placeholder={t('orders.customerNumber')}
            value={mobileNumber}
            onChange={(e) => handleMobileNumberChange(e.target.value)}
            sx={{ minWidth: 140 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              value={statusFilter}
              onChange={(e) => handleStatusFilterChange(e.target.value as OrderStatus | '')}
              displayEmpty
              renderValue={(selected) => {
                if (!selected) {
                  return <span style={{ color: '#999' }}>{t('orders.status')}</span>;
                }
                return t(`orders.${selected.toLowerCase()}`);
              }}
            >
              <MenuItem value="">{t('common.all')}</MenuItem>
              <MenuItem value="CREATED">{t('orders.created')}</MenuItem>
              <MenuItem value="BOOKED">{t('orders.booked')}</MenuItem>
              <MenuItem value="CANCELED">{t('orders.canceled')}</MenuItem>
              <MenuItem value="COMPLETED">{t('orders.completed')}</MenuItem>
            </Select>
          </FormControl>
          <DateRangeFilter
            dateFrom={dateFrom}
            dateTo={dateTo}
            onDateFromChange={handleDateFromChange}
            onDateToChange={handleDateToChange}
          />
          {canAccessEmployeeFilter && (
            <Autocomplete
              dir={direction}
              size="small"
              options={employees}
              getOptionLabel={(option) => option.name}
              value={employees.find((e) => e.id === selectedEmployeeId) || null}
              onChange={(_, newValue) => {
                handleEmployeeFilterChange(newValue ? newValue.id : null);
              }}
              noOptionsText={t('common.noOptions')}
              slotProps={{
                popupIndicator: { sx: { display: 'none' } },
                clearIndicator: {
                  sx: {
                    p: 0.5,
                      border: 0,
                      backgroundColor: "transparent",
                    '&:hover': { backgroundColor: 'action.hover' },
                  },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t('orders.employee') || 'Employee'}
                  inputProps={{
                    ...params.inputProps,
                    style: { textAlign: direction === 'rtl' ? 'right' : 'left' },
                  }}
                />
              )}
              sx={{ minWidth: 180, flex: '0 1 auto' }}
            />
          )}
        </Box>
        {error ? (
          <Box sx={{ flexGrow: 1 }}>
            <Alert severity="error">{error.message}</Alert>
          </Box>
        ) : (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              minHeight: 0,
            }}
          >
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
              }}
            >
              <DataGrid
                rows={rowsState.rows}
                rowCount={rowsState.rowCount}
                columns={columns}
                pagination
                paginationMode="server"
                paginationModel={paginationModel}
                onPaginationModelChange={handlePaginationModelChange}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
                disableRowSelectionOnClick
                disableColumnFilter
                disableColumnMenu
                disableColumnSelector
                onRowClick={handleRowClick}
                loading={isLoading}
                initialState={initialState}
                pageSizeOptions={[INITIAL_PAGE_SIZE, 50, 75, 100]}
                hideFooter
                localeText={{
                  noRowsLabel: t('dataGrid.noRowsLabel'),
                  noResultsOverlayLabel: t('dataGrid.noResultsOverlayLabel'),
                }}
                slotProps={{
                  loadingOverlay: {
                    variant: 'circular-progress',
                    noRowsVariant: 'circular-progress',
                  },
                  baseIconButton: {
                    size: 'small',
                  },
                }}
                sx={{
                  height: '100%',
                  width: '100%',
                  [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: {
                    outline: 'transparent',
                  },
                  [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]:
                    {
                      outline: 'none',
                    },
                  [`& .${gridClasses.row}:hover`]: {
                    cursor: 'pointer',
                  },
                  // Hide column separator (resize handle)
                  [`& .${gridClasses.columnSeparator}`]: {
                    display: 'none',
                  },
                  // Disable column header dragging
                  [`& .${gridClasses.columnHeader}`]: {
                    cursor: 'default !important',
                  },
                }}
              />
            </Box>
            <CustomTablePagination
              paginationModel={paginationModel}
              rowCount={rowsState.rowCount}
              onPaginationModelChange={handlePaginationModelChange}
              pageSizeOptions={[INITIAL_PAGE_SIZE, 50, 75, 100]}
            />
          </Box>
        )}
      </Box>
      
      {/* Actions Menu */}
      {selectedOrder && selectedOrder.status !== 'CANCELED' && selectedOrder.status !== 'COMPLETED' && (
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: isRtl ? 'left' : 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: isRtl ? 'left' : 'right',
        }}
      >
        {hasRequiredAmounts(selectedOrder) && (
          <MenuItem onClick={handlePayNow}>
            <ListItemIcon>
              <PaymentIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {selectedOrder && (typeof selectedOrder.paid_amount === 'string'
                ? parseFloat(selectedOrder.paid_amount)
                : selectedOrder.paid_amount || 0) > 0
                ? t('orders.payInstallment')
                : t('orders.payNow')}
            </ListItemText>
          </MenuItem>
        )}
          {selectedOrder.status === 'CREATED' && (
            <MenuItem onClick={handleCancelOrder}>
              <ListItemIcon>
                <CancelIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('orders.cancelOrder')}</ListItemText>
            </MenuItem>
          )}
        {/*<MenuItem onClick={() => selectedOrder && handleRowDelete(selectedOrder)}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('common.delete')}</ListItemText>
        </MenuItem>*/}
      </Menu>
      )}

      {/* Payment Dialog */}
      {selectedOrder && (
        <PaymentDialog
          open={paymentDialogOpen}
          payload={{
            orderId: selectedOrder.id,
            totalAmount: typeof selectedOrder.full_amount === 'string'
              ? parseFloat(selectedOrder.full_amount)
              : selectedOrder.full_amount,
            pendingAmount: typeof selectedOrder.pending_amount === 'string'
              ? parseFloat(selectedOrder.pending_amount)
              : selectedOrder.pending_amount,
            orderStatus: selectedOrder.status,
          }}
          onClose={handlePaymentDialogClose}
        />
      )}

      {/* Installment Selection Dialog */}
      {selectedOrder && (
        <InstallmentSelectionDialog
          open={installmentSelectionDialogOpen}
          orderId={selectedOrder.id}
          onClose={() => {
            setInstallmentSelectionDialogOpen(false);
            setTimeout(() => setSelectedOrder(null), 100);
          }}
          onSuccess={() => {
            setInstallmentSelectionDialogOpen(false);
            setTimeout(() => setSelectedOrder(null), 100);
            loadData();
          }}
        />
      )}

      {/* Cancel Order Dialog */}
      <CancelOrderDialog
        open={cancelDialogOpen}
        orderId={selectedOrder?.id || null}
        onClose={handleCancelDialogClose}
        onSuccess={handleCancelSuccess}
      />

      {/* Column Visibility Popover */}
      <Popover
        open={Boolean(columnMenuAnchor)}
        anchorEl={columnMenuAnchor}
        onClose={handleColumnMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: isRtl ? 'left' : 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: isRtl ? 'left' : 'right',
        }}
      >
        <Box sx={{ p: 2, minWidth: 200 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Show/Hide Columns
          </Typography>
          <Stack spacing={0.5}>
            {availableColumns.map((col) => (
              <FormControlLabel
                key={col.field}
                control={
                  <Checkbox
                    checked={columnVisibilityModel[col.field] !== false}
                    onChange={() => handleToggleColumn(col.field)}
                    size="small"
                  />
                }
                label={col.label}
                sx={{
                  '& .MuiFormControlLabel-label': {
                    fontSize: '0.875rem'
                  }
                }}
              />
            ))}
          </Stack>
        </Box>
      </Popover>
    </PageContainer>
  );
}

